import { useState, useEffect } from 'react';
import { settingsApi } from '../../utils/api';
import LoadingSpinner from '../../components/LoadingSpinner';

/**
 * AdminSettings Component
 * 
 * Provides admin interface for managing:
 * - Google Analytics Measurement ID
 * - SEO default settings (title, description, keywords)
 * - Open Graph image
 * - Twitter handle
 * - Analytics enable/disable toggle
 */
export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await settingsApi.getAll();
      // Convert array to object for easier access
      const settingsObj = {};
      data.settings.forEach(s => {
        settingsObj[s.key] = s.value || '';
      });
      setSettings(settingsObj);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key, value) => {
    setSaving(true);
    setMessage(null);
    
    try {
      await settingsApi.update(key, value);
      setSettings(prev => ({ ...prev, [key]: value }));
      setMessage({ type: 'success', text: 'Setting saved successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSave = async (updates) => {
    setSaving(true);
    setMessage(null);
    
    try {
      await settingsApi.bulkUpdate(updates);
      setSettings(prev => ({ ...prev, ...updates }));
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: '📊' },
    { id: 'seo', label: 'SEO', icon: '🔍' },
    { id: 'social', label: 'Social Media', icon: '📱' },
  ];

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-gray-900 mb-2">Site Settings</h1>
      <p className="text-gray-600 mb-8">Configure analytics, SEO, and social media settings</p>

      {/* Status message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <AnalyticsSettings 
          settings={settings} 
          onSave={handleSave}
          onBulkSave={handleBulkSave}
          saving={saving}
        />
      )}

      {/* SEO Tab */}
      {activeTab === 'seo' && (
        <SEOSettings 
          settings={settings} 
          onSave={handleSave}
          onBulkSave={handleBulkSave}
          saving={saving}
        />
      )}

      {/* Social Media Tab */}
      {activeTab === 'social' && (
        <SocialSettings 
          settings={settings} 
          onSave={handleSave}
          onBulkSave={handleBulkSave}
          saving={saving}
        />
      )}
    </div>
  );
}

// ============================================
// ANALYTICS SETTINGS
// ============================================
function AnalyticsSettings({ settings, onSave, saving }) {
  const [gaId, setGaId] = useState(settings.google_analytics_id || '');
  const [enabled, setEnabled] = useState(settings.enable_analytics !== 'false');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setGaId(settings.google_analytics_id || '');
    setEnabled(settings.enable_analytics !== 'false');
  }, [settings]);

  const handleGaIdChange = (e) => {
    setGaId(e.target.value.toUpperCase());
    setHasChanges(true);
  };

  const handleToggle = async () => {
    const newValue = !enabled;
    setEnabled(newValue);
    await onSave('enable_analytics', newValue ? 'true' : 'false');
  };

  const handleSaveGaId = async () => {
    await onSave('google_analytics_id', gaId);
    setHasChanges(false);
  };

  const isValidGaId = !gaId || /^G-[A-Z0-9]+$/.test(gaId);

  return (
    <div className="space-y-6">
      {/* Google Analytics Card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <img 
                src="https://www.google.com/favicon.ico" 
                alt="Google" 
                className="w-5 h-5 mr-2"
              />
              Google Analytics
            </h2>
            <p className="text-gray-600 mt-1">
              Track website traffic and user behavior
            </p>
          </div>
          
          {/* Enable/Disable Toggle */}
          <button
            onClick={handleToggle}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              enabled ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Measurement ID
            </label>
            <div className="flex space-x-3">
              <input
                type="text"
                value={gaId}
                onChange={handleGaIdChange}
                placeholder="G-XXXXXXXXXX"
                className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono ${
                  !isValidGaId ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              <button
                onClick={handleSaveGaId}
                disabled={saving || !isValidGaId || !hasChanges}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
            {!isValidGaId && (
              <p className="text-red-500 text-sm mt-1">
                Invalid format. Must be like G-XXXXXXXXXX
              </p>
            )}
            <p className="text-gray-500 text-sm mt-2">
              Find this in Google Analytics → Admin → Data Streams → Your Stream → Measurement ID
            </p>
          </div>

          {/* Status indicator */}
          <div className={`p-4 rounded-lg ${
            gaId && enabled 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-gray-50 border border-gray-200'
          }`}>
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                gaId && enabled ? 'bg-green-500' : 'bg-gray-400'
              }`} />
              <div>
                <p className="font-medium text-gray-900">
                  {gaId && enabled 
                    ? 'Analytics Active' 
                    : gaId 
                      ? 'Analytics Disabled' 
                      : 'Not Configured'}
                </p>
                <p className="text-sm text-gray-600">
                  {gaId && enabled 
                    ? `Tracking with ${gaId}` 
                    : gaId 
                      ? 'Enable the toggle above to start tracking'
                      : 'Enter your Measurement ID to enable tracking'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How to get GA ID */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h3 className="font-bold text-blue-900 mb-3">📘 How to get your Google Analytics ID</h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-800">
          <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline">analytics.google.com</a></li>
          <li>Create an account if you don't have one</li>
          <li>Set up a new property for SpeechFormation</li>
          <li>Go to Admin → Data Streams → Add Stream → Web</li>
          <li>Enter your website URL: <code className="bg-blue-100 px-1 rounded">speechformation.com</code></li>
          <li>Copy the Measurement ID (starts with G-)</li>
          <li>Paste it above and click Save</li>
        </ol>
      </div>
    </div>
  );
}

// ============================================
// SEO SETTINGS
// ============================================
function SEOSettings({ settings, onBulkSave, saving }) {
  const [formData, setFormData] = useState({
    site_title: settings.site_title || '',
    site_description: settings.site_description || '',
    site_keywords: settings.site_keywords || ''
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData({
      site_title: settings.site_title || '',
      site_description: settings.site_description || '',
      site_keywords: settings.site_keywords || ''
    });
  }, [settings]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onBulkSave(formData);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">🔍 Search Engine Optimization</h2>
        
        <div className="space-y-6">
          {/* Site Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Page Title
            </label>
            <input
              type="text"
              value={formData.site_title}
              onChange={(e) => handleChange('site_title', e.target.value)}
              placeholder="SpeechFormation - Speech Practice Through Play"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              maxLength={70}
            />
            <p className="text-gray-500 text-sm mt-1">
              {formData.site_title.length}/70 characters (recommended max for Google)
            </p>
          </div>

          {/* Meta Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Meta Description
            </label>
            <textarea
              value={formData.site_description}
              onChange={(e) => handleChange('site_description', e.target.value)}
              placeholder="Voice-controlled games that make speech practice feel like play, not therapy."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              maxLength={160}
            />
            <p className="text-gray-500 text-sm mt-1">
              {formData.site_description.length}/160 characters (recommended max for Google)
            </p>
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Keywords
            </label>
            <textarea
              value={formData.site_keywords}
              onChange={(e) => handleChange('site_keywords', e.target.value)}
              placeholder="speech therapy, autism, speech delay, voice games..."
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <p className="text-gray-500 text-sm mt-1">
              Comma-separated keywords for search engines
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save SEO Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* SEO Preview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-bold text-gray-900 mb-4">Google Search Preview</h3>
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="text-blue-600 text-lg hover:underline cursor-pointer">
            {formData.site_title || 'Your Page Title'}
          </div>
          <div className="text-green-700 text-sm">
            https://speechformation.com
          </div>
          <div className="text-gray-600 text-sm mt-1">
            {formData.site_description || 'Your meta description will appear here...'}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// SOCIAL MEDIA SETTINGS
// ============================================
function SocialSettings({ settings, onBulkSave, saving }) {
  const [formData, setFormData] = useState({
    og_image: settings.og_image || '',
    twitter_handle: settings.twitter_handle || ''
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData({
      og_image: settings.og_image || '',
      twitter_handle: settings.twitter_handle || ''
    });
  }, [settings]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onBulkSave(formData);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">📱 Social Media Sharing</h2>
        
        <div className="space-y-6">
          {/* Open Graph Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Share Image (Open Graph)
            </label>
            <input
              type="text"
              value={formData.og_image}
              onChange={(e) => handleChange('og_image', e.target.value)}
              placeholder="/og-image.jpg"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-gray-500 text-sm mt-1">
              Image shown when your site is shared on Facebook, LinkedIn, etc. 
              Recommended size: 1200x630 pixels
            </p>
          </div>

          {/* Twitter Handle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Twitter Handle
            </label>
            <input
              type="text"
              value={formData.twitter_handle}
              onChange={(e) => handleChange('twitter_handle', e.target.value)}
              placeholder="@speechformation"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-gray-500 text-sm mt-1">
              Used for Twitter Card attribution
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Social Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Social Preview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-bold text-gray-900 mb-4">Social Share Preview</h3>
        <div className="border rounded-lg overflow-hidden max-w-md">
          <div className="bg-gray-200 h-48 flex items-center justify-center">
            {formData.og_image ? (
              <img 
                src={formData.og_image.startsWith('http') ? formData.og_image : `https://speechformation.com${formData.og_image}`}
                alt="OG Preview"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`text-gray-500 ${formData.og_image ? 'hidden' : 'flex'} items-center justify-center w-full h-full`}>
              <span>No image configured</span>
            </div>
          </div>
          <div className="p-4 bg-white">
            <div className="text-xs text-gray-500 uppercase">speechformation.com</div>
            <div className="font-bold text-gray-900 mt-1">{settings.site_title || 'SpeechFormation'}</div>
            <div className="text-gray-600 text-sm mt-1 line-clamp-2">
              {settings.site_description || 'Your description here...'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
